/**
 * The shareable result (spec phase 6).
 *
 * A Wordle grid works because it proves you played without spoiling anything.
 * The same rule applies here and it is stricter than it looks: the grid may
 * encode *how far you climbed*, never which countries came up. Naming the
 * countries would hand the day's seed to everyone who reads the post, and the
 * daily leaderboard is only legitimate because one attempt makes memorisation
 * impossible.
 *
 * THE LAST LINE IS THE LINK, and it is what makes the rest of it earn its keep.
 * Wordle could post a bare grid because everybody already knew where to go and
 * play one; this grid does not have that luxury, and a share with no
 * destination is a dead end. Every posted result is the cheapest distribution
 * this game has.
 *
 * `host` is PASSED IN — never read off `window` in here, and never written down
 * as a literal. Two reasons, the second load-bearing:
 *
 *   - `shareText` is pure, and a pure function that reaches for a global is
 *     only pure until somebody unit-tests it.
 *   - `nohosts.test.ts` fails the suite on any remote host named in live code,
 *     and a hardcoded domain here is precisely the thing it should catch. The
 *     origin the player is already on is the better answer anyway: it follows
 *     the app to a preview deploy, to a renamed domain and to localhost with
 *     nobody having to remember to update it.
 */

import type { RoundEndMessage, Tier } from '@geogame/shared';

import { outcomeEmoji, points, tierLabel, todayKey } from './format';

export function emojiGrid(
  history: readonly RoundEndMessage[],
  ladderDepth: number,
): string {
  return history
    .map((r) => outcomeEmoji(r.you.outcome, r.you.finalRung, ladderDepth))
    .join('');
}

export interface ShareInput {
  readonly mode: 'daily' | 'quick' | 'group' | 'friend' | 'practice';
  readonly tier: Tier;
  readonly total: number;
  readonly opponentTotal: number;
  readonly history: readonly RoundEndMessage[];
  readonly ladderDepth: number;
  readonly dateKey?: string;
  /**
   * The bare host to send the reader to — `location.host`, so it keeps the dev
   * port and stays right on every deploy. Omitted, the text just ends at the
   * grid, which is what every caller did before this field existed.
   */
  readonly host?: string;
}

export function shareText(input: ShareInput): string {
  const grid = emojiGrid(input.history, input.ladderDepth);
  const banked = input.history.filter((r) => r.you.banked).length;
  const head =
    input.mode === 'daily'
      ? `Worldly Daily ${input.dateKey ?? todayKey()} · ${tierLabel(input.tier)}`
      : `Worldly · ${tierLabel(input.tier)}`;
  const verdict =
    input.total > input.opponentTotal
      ? 'won'
      : input.total < input.opponentTotal
        ? 'lost'
        : 'drew';
  const line =
    input.mode === 'daily'
      ? `${points(input.total)} · ${banked}/${input.history.length} banked`
      : `${points(input.total)} · ${verdict} ${points(input.total)}–${points(input.opponentTotal)}`;

  const link = input.host !== undefined && input.host !== '' ? `\n${input.host}` : '';
  return `${head}\n${line}\n${grid}${link}`;
}

export type ShareResult = 'shared' | 'copied' | 'failed';

export async function shareOrCopy(text: string): Promise<ShareResult> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ text });
      return 'shared';
    } catch {
      // The user dismissed the sheet, or the platform refused. Fall through to
      // the clipboard rather than telling them it failed.
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
